import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import type { ComponentProps } from 'react'
import { memo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import config from 'constants/config'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useWishlistIndex } from 'wishlists/hooks'
import { CHAT_TRIP_PLANNER_ROUTE } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useChatNavigation'
import NavBarButton from '../NavBarButton/NavBarButton'

const TripNavButton = styled(NavBarButton)`

    ${mediaQueryUp.tablet} {
      display: flex;
    }

`

type InheritedNavBarButtonProps = Pick<ComponentProps<typeof NavBarButton>, 'contentBreakpointMax'>

interface Props extends InheritedNavBarButtonProps {
  className?: string
}

function NavBarTripButton(props: Props) {
  if (!config.ENABLE_TRIP_PLANNER) return null

  return <NavBarTripButtonInner {...props} />
}

function NavBarTripButtonInner(props: Props) {
  const isLoggedIn = useAppSelector(selectLoggedIn)

  if (!isLoggedIn) return <AITripPlannerNavButton {...props} />

  return <>
    <CSSBreakpoint min="tablet" max="tablet" as="span">
      <WishlistNavButton {...props} />
    </CSSBreakpoint>
    <AITripPlannerNavButton {...props} />
  </>
}

function AITripPlannerNavButton(props: Props) {
  const intl = useIntl()

  const aiTripPlannerText = intl.formatMessage({
    id: 'EYQsWpC_SwW6ZS0Xb0Yz',
    defaultMessage: 'AI TripPlanner',
    description: 'Branding label for the AI TripPlanner feature in the header',
  })

  return (
    <CSSBreakpoint min="desktop" as="span">
      <TripNavButton
        {...props}
        icon={<SolidSparklesIcon />}
        contentBreakpointMin="desktop"
        to={CHAT_TRIP_PLANNER_ROUTE}
        target="_blank"
      >
        {aiTripPlannerText}
      </TripNavButton>
    </CSSBreakpoint>
  )
}

function WishlistNavButton(props: Props) {
  const intl = useIntl()
  const { data } = useWishlistIndex()
  const hasSavedItems = Boolean(
    data?.wishlists.find((wishlist) => wishlist.isDefault)?.itemCount,
  )

  const wishlistText = intl.formatMessage({
    id: '8tn_OvJmXrdKVNnw4EQO',
    defaultMessage: 'Wishlist',
    description: 'Wishlist navigation button text in the website header',
  })

  return (
    <TripNavButton
      {...props}
      icon={hasSavedItems ? <SolidHeartIcon colour="favourite" /> : <LineHeartIcon />}
      contentBreakpointMin="desktop"
      to="/wishlists"
    >
      {wishlistText}
    </TripNavButton>
  )
}

export default memo(NavBarTripButton)
